import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import type { GetGameResponse } from '@/tygo/dto'
import { getGame, joinGame } from '@/domains/game/api'
import { BuyInForm, buyInFormOpts } from '@/domains/game/form/buy-in-form'
import { useAppForm } from '@/lib/form'
import { useBalance } from '@/domains/balance/hooks/useBalance'
import { authQueryOptions } from '@/domains/auth/queries'
import { dollarsToMicroUSDC, microUSDCToDollars } from '@/lib/currency'
import { HttpError } from '@/lib/http'

export const Route = createFileRoute('/game/join/$id')({
  component: JoinGamePage,
  beforeLoad: ({ context, location }) => {
    const { queryClient } = context
    const authData = queryClient.getQueryData(authQueryOptions('').queryKey)
    if (!authData) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      })
    }
  },
})

function JoinGamePage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const balance = useBalance()

  const [gameDetails, setGameDetails] = useState<GetGameResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [buyInDialogOpen, setBuyInDialogOpen] = useState(false)
  const [isJoining, setIsJoining] = useState(false)

  const form = useAppForm({
    ...buyInFormOpts,
    onSubmit: async ({ value }) => {
      if (!gameDetails) return

      const buyInMicro =
        gameDetails.min_buy_in === gameDetails.max_buy_in
          ? gameDetails.min_buy_in
          : dollarsToMicroUSDC(parseFloat(value.buyInAmount))

      setIsJoining(true)
      try {
        await joinGame(id, buyInMicro)
        navigate({ to: '/game/$id', params: { id } })
      } catch (error) {
        if (error instanceof HttpError) {
          if (error.message.includes('leave your current game')) {
            navigate({ to: '/game/$id', params: { id } })
            return
          }
          toast.error(error.message)
        } else {
          toast.error('Failed to join game')
        }
        setIsJoining(false)
      }
    },
  })

  useEffect(() => {
    let cancelled = false

    async function fetchAndValidate() {
      try {
        const game = await getGame(id)
        if (cancelled) return

        if (
          game.game_status !== 'waiting' &&
          game.game_status !== 'in_progress'
        ) {
          toast.error('This game is no longer available')
          navigate({ to: '/' })
          return
        }

        const playerCount = game.game_seats.filter((s) => s.player).length
        if (playerCount >= game.player_limit) {
          toast.error('This game is full')
          navigate({ to: '/' })
          return
        }

        setGameDetails(game)
        setIsLoading(false)
      } catch {
        toast.error('Game not found')
        navigate({ to: '/' })
      }
    }

    fetchAndValidate()
    return () => {
      cancelled = true
    }
  }, [id, navigate])

  // Check balance once we have game details and balance
  useEffect(() => {
    if (!gameDetails || balance === undefined) return

    if (gameDetails.min_buy_in > balance) {
      toast.error(
        `You need at least $${microUSDCToDollars(gameDetails.min_buy_in).toFixed(2)} to join this game`,
      )
      navigate({ to: '/' })
      return
    }

    form.setFieldValue(
      'buyInAmount',
      microUSDCToDollars(gameDetails.min_buy_in).toFixed(2),
    )
    setBuyInDialogOpen(true)
  }, [gameDetails, balance, navigate])

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-muted-foreground">Loading game...</p>
      </div>
    )
  }

  return (
    <div className="flex-1 flex items-center justify-center">
      <BuyInForm
        form={form}
        game={gameDetails}
        balance={balance ?? 0}
        open={buyInDialogOpen}
        onOpenChange={(open) => {
          if (!open && !isJoining) {
            navigate({ to: '/' })
          }
        }}
      />
    </div>
  )
}
